import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Текущая страница списка в URL (?page=N, с 1) — переживает перезагрузку и открывается по ссылке.
 * Остальные параметры адреса (поиск, фильтры) не затираются.
 *
 * @param {number} pageSize — элементов на странице
 * @param {string} [param='page'] — имя параметра в URL (для нескольких списков на одной странице)
 * @returns {{ page: number, offset: number, pageSize: number, setPage: (page: number) => void, resetPage: () => void }}
 */
export const useUrlPagination = (pageSize = 10, param = 'page') => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useMemo(() => {
    const value = Number(searchParams.get(param));
    return Number.isInteger(value) && value > 0 ? value : 1;
  }, [searchParams, param]);

  const setPage = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next > 1) params.set(param, String(next));
          else params.delete(param);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams, param],
  );

  // Новый поиск/фильтр — снова с первой страницы
  const resetPage = useCallback(() => setPage(1), [setPage]);

  return { page, offset: (page - 1) * pageSize, pageSize, setPage, resetPage };
};
